import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "../../api/axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGithub } from "@fortawesome/free-brands-svg-icons";
import { Gallery } from "../Gallery/Gallery";
import { Helmet } from "react-helmet";
import "./Event-Page.css";
import "react-multi-carousel/lib/styles.css";

const EventOverview = ({ overview }) =>
  overview ? (
    <section className="events-page-info">
      <h2 className="events-vertical-name-text">Overview</h2>
      <p>{overview}</p>
    </section>
  ) : null;

const EventTechnologies = ({ tech }) =>
  tech ? (
    <section className="events-page-info" id="bottom-box">
      <h2 className="events-vertical-name-text">Technologies Used</h2>
      <ul>
        {tech.map((item, index) => (
          <li className="events-page-features-text" key={index}>
            {item}
          </li>
        ))}
      </ul>
    </section>
  ) : null;

const EventFeatures = ({ features }) =>
  features ? (
    <section className="events-page-info" id="bottom-box">
      <h2 className="events-vertical-name-text">Features</h2>
      <ul>
        {features.map((item, index) => (
          <li className="events-page-features-text" key={index}>
            {item}
          </li>
        ))}
      </ul>
    </section>
  ) : null;

const EventLinks = ({ link, github, type, name }) =>
  link || github ? (
    <div className="events-buttons">
      {link && (
        <a href={link} target="_blank" rel="noopener noreferrer" aria-label={`See live project: ${name}`}>
          <button className="events-button" type="button">
            See Live
          </button>
        </a>
      )}
      {type !== "Websites" && github && (
        <a href={github} target="_blank" rel="noopener noreferrer" aria-label={`GitHub repository for ${name}`}>
          <FontAwesomeIcon className="events-github-button" icon={faGithub} />
        </a>
      )}
    </div>
  ) : null;

const EventPage = ({ data }) => {
  const hasMedia = data.media && data.media.length > 0;

  return (
    <main className="events-page-main">
      <Helmet>
        <title>{`Projects || ${data.name} || Karan Kumar Agrawal`}</title>
        <meta name="description" content={data.headline || `Details and overview of the project ${data.name} by Karan Kumar Agrawal.`} />
        <meta name="keywords" content={`Karan Kumar Agrawal, Project, ${data.name}, Portfolio, Full Stack Developer`} />
        <meta name="robots" content="index, follow" />

        {/* JSON-LD structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Project",
            "name": data.name,
            "description": data.headline,
            "url": `/projects/${data.name.toLowerCase().replace(/\s+/g, "")}`,
            "keywords": data.desc?.tech?.join(", "),
          })}
        </script>
      </Helmet>

      <section className="events-page-cards-row">
        <article className="events-page-horizontal-card">
          <div className="events-page-vertical-card-bottom">
            <h1 className="events-vertical-name-text">{data.name}</h1>
            <p className="events-page-headline-text">{data.headline}</p>
          </div>
          <EventLinks link={data.link} github={data.github} type={data.type} name={data.name} />
        </article>
      </section>

      <EventOverview overview={data.desc?.overview} />
      <div className="events-page-bottom-row">
        <EventTechnologies tech={data.desc?.tech} />
        <EventFeatures features={data.desc?.features} />
      </div>

      {hasMedia && <Gallery media={data.media} />}
    </main>
  );
};

const ProjectDetail = () => {
  const [projects, setProjects] = useState([]);
  const { projectName } = useParams();

  useEffect(() => {
    const fetchProjectData = async () => {
      try {
        const response = await axios.get(`/data/projects`);
        setProjects(response.data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchProjectData();
  }, []);

  const project = useMemo(
    () =>
      projects.find(
        (p) =>
          p.name.toLowerCase().replace(/\s+/g, "") ===
          projectName.toLowerCase().replace(/\s+/g, "")
      ),
    [projects, projectName]
  );

  if (!project) return <div>Project not found</div>;

  return <EventPage data={project} />;
};

export default ProjectDetail;
